import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
} from '@chakra-ui/modal';
import { FaExclamationTriangle, FaCalendarAlt, FaBox } from 'react-icons/fa';

interface ExistingOrderModalProps {
    isOpen: boolean;
    onClose: () => void;
    orders: any[];
    selectedDate: string;
    onContinue: () => void;
}

const ExistingOrderModal = (props: ExistingOrderModalProps) => {
    const { isOpen, onClose, orders, selectedDate, onContinue } = props;

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const handleContinue = () => {
        onContinue();
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
            <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
            <ModalContent className="!bg-white !rounded-2xl !shadow-2xl !mx-4 !my-4 sm:!my-8 !max-h-[70vh] sm:!max-h-[75vh]">
                <ModalHeader className="!pb-2 !pt-4 !px-4 sm:!px-5 !pr-12 sm:!pr-14">
                    <div className="flex items-start gap-2">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FaExclamationTriangle className="text-amber-600 text-base sm:text-lg" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-base sm:text-lg font-bold text-slate-800 leading-tight">
                                Commande existante détectée
                            </h3>
                            <p className="text-xs text-slate-500 mt-0.5">
                                Vous avez déjà une commande prévue pour cette date
                            </p>
                        </div>
                    </div>
                </ModalHeader>
                <ModalCloseButton className="!top-3 !right-3 sm:!top-4 sm:!right-4 !text-slate-400 hover:!text-slate-600 !w-7 !h-7 sm:!w-8 sm:!h-8" />

                <ModalBody className="!px-4 sm:!px-5 !pb-3 sm:!pb-4 !overflow-y-auto">
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-2.5 sm:p-3 mb-3 border border-amber-200">
                        <div className="flex items-start gap-2 mb-1">
                            <FaCalendarAlt className="text-amber-600 flex-shrink-0 mt-0.5 text-sm" />
                            <span className="font-semibold text-slate-800 text-xs sm:text-sm">
                                Date de livraison : {formatDate(selectedDate)}
                            </span>
                        </div>
                        <p className="text-xs text-slate-600">
                            {orders.length} commande{orders.length > 1 ? 's' : ''} trouvée{orders.length > 1 ? 's' : ''}
                        </p>
                    </div>

                    {/* Existing orders list */}
                    <div className="space-y-2 mb-3 max-h-[25vh] sm:max-h-[30vh] overflow-y-auto">
                        {orders.map((order: any, index: number) => (
                            <div
                                key={order._id}
                                className="bg-white border border-slate-200 rounded-lg p-2.5 sm:p-3 hover:border-blue-300 transition-colors"
                            >
                                <div className="flex items-start justify-between mb-2 gap-2">
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-slate-800 text-xs sm:text-sm">
                                            Commande #{order.orderId || 'En attente'}
                                        </p>
                                        <p className="text-xs text-slate-500 mt-0.5">
                                            Créée le {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                                        </p>
                                    </div>
                                    <div className={`px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                                        order.status === 'pending' 
                                            ? 'bg-blue-100 text-blue-700'
                                            : order.status === 'delivered'
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-slate-100 text-slate-700'
                                    }`}>
                                        {order.status === 'pending' ? 'En cours' : 
                                         order.status === 'delivered' ? 'Livrée' : order.status}
                                    </div>
                                </div>

                                {/* Existing items list */}
                                {order.items && order.items.length > 0 && (
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-1.5 text-xs font-medium text-slate-700 mb-1">
                                            <FaBox className="text-slate-400 flex-shrink-0 text-xs" />
                                            <span>{order.items.length} article{order.items.length > 1 ? 's' : ''}</span>
                                        </div>
                                        <div className="space-y-1 max-h-24 overflow-y-auto">
                                            {order.items.map((item: any, idx: number) => (
                                                <div
                                                    key={idx}
                                                    className="flex items-center justify-between py-1.5 px-2 bg-slate-50 rounded text-xs gap-2"
                                                >
                                                    <span className="text-slate-700 flex-1 min-w-0 truncate">{item.name}</span>
                                                    <span className="text-slate-600 font-medium flex-shrink-0 whitespace-nowrap">
                                                        {item.quantity} {item.unity}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {order.totalPrice > 0 && (
                                    <div className="mt-2 pt-2 border-t border-slate-200 flex justify-between items-center">
                                        <span className="text-xs text-slate-600">Total HT</span>
                                        <span className="font-bold text-xs sm:text-sm text-blue-600">{order.totalPrice.toFixed(2)}€</span>
                                    </div>
                                )}

                                {order.customerNotes && (
                                    <div className="mt-2 pt-2 border-t border-slate-200">
                                        <p className="text-xs text-slate-500 mb-0.5">Note de livraison :</p>
                                        <p className="text-xs text-slate-700 italic break-words">{order.customerNotes}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Actions */}
                    <div className="bg-slate-50 rounded-lg p-2.5 sm:p-3 space-y-2 sticky bottom-0">
                        <p className="text-xs text-slate-600 text-center">
                            Souhaitez-vous créer une nouvelle commande pour cette même date ?
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <button
                                onClick={onClose}
                                className="flex-1 px-3 py-2 bg-white hover:bg-slate-100 text-slate-700 rounded-lg text-xs sm:text-sm font-semibold border border-slate-300 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleContinue}
                                className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg text-xs sm:text-sm font-semibold shadow-lg hover:shadow-xl transition-all"
                            >
                                Continuer quand même
                            </button>
                        </div>
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default ExistingOrderModal;
